import React, { useState, useEffect } from 'react';
import { FaHeart, FaStar, FaMusic, FaRing, FaEnvelope, FaCalendarAlt, FaInfinity } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import sabariKalaiPhoto from '../assets/sabari_kalai.jpeg';

const ValentinePage = () => {
  const [hearts, setHearts] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const [currentWish, setCurrentWish] = useState(0);
  const [showEnvelope, setShowEnvelope] = useState(true);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [petals, setPetals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentCard, setCurrentCard] = useState(0);

  const wishes = [
    "My Love Kalai! 💖",
    "You are the meaning of my life! 🌹",
    "Your smile is my world! 😊",
    "Forever with you! 💕",
    "You make my heart complete! 💝"
  ];

  const loveQuotes = [
    "You are my backbone, my strength, my everything! 💪❤️",
    "In you, I found my home, my peace, my forever! 🏠💕",
    "You're not just my love, you're my life's purpose! 🌟",
    "Distance means nothing when someone means everything! 💫",
    "My heart beats only for you, my Kalai! 💓"
  ];

  useEffect(() => {
    // Create floating hearts
    const heartArray = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 5 + Math.random() * 5
    }));
    setHearts(heartArray);

    // Create rose petals
    const petalArray = Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 8 + Math.random() * 7
    }));
    setPetals(petalArray);

    // Show message after delay
    setTimeout(() => setShowMessage(true), 1500);

    // Rotate wishes
    const wishInterval = setInterval(() => {
      setCurrentWish((prev) => (prev + 1) % wishes.length);
    }, 3000);

    // Rotate quotes
    const quoteInterval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % loveQuotes.length);
    }, 4000);

    return () => {
      clearInterval(wishInterval);
      clearInterval(quoteInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEnvelope = () => {
    setIsLoading(true);
    
    // Animate progress bar
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 2;
      setLoadingProgress(progress);
      if (progress >= 100) {
        clearInterval(progressInterval);
      }
    }, 30);

    // Show content after loading
    setTimeout(() => {
      setShowEnvelope(false);
      setIsLoading(false);
      setTimeout(() => setShowMessage(true), 500);
    }, 3000);
  };

  const nextCard = () => {
    setCurrentCard(prev => prev + 1);
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const previousCard = () => {
    setCurrentCard(prev => Math.max(0, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-950 via-pink-900 to-fuchsia-950 relative overflow-hidden">
      {/* Animated Background with Stars */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-96 h-96 bg-rose-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-fuchsia-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Twinkling Stars */}
      {Array.from({ length: 15 }).map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute text-yellow-200 animate-twinkle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            fontSize: `${8 + Math.random() * 8}px`
          }}
        >
          ✨
        </div>
      ))}

      {/* Floating Hearts Background */}
      {hearts.map((heart) => (
        <div
          key={heart.id}
          className="absolute text-pink-400 opacity-40 animate-float"
          style={{
            left: `${heart.left}%`,
            bottom: '-50px',
            animationDelay: `${heart.delay}s`,
            animationDuration: `${heart.duration}s`,
            fontSize: `${20 + Math.random() * 30}px`
          }}
        >
          ❤️
        </div>
      ))}

      {/* Falling Rose Petals */}
      {petals.map((petal) => (
        <div
          key={`petal-${petal.id}`}
          className="absolute text-rose-400 opacity-50 animate-fall"
          style={{
            left: `${petal.left}%`,
            top: '-50px',
            animationDelay: `${petal.delay}s`,
            animationDuration: `${petal.duration}s`,
            fontSize: `${15 + Math.random() * 20}px`
          }}
        >
          🌸
        </div>
      ))}

      {/* Envelope Animation */}
      {showEnvelope && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md animate-fade-in px-4">
          <div className="text-center">
            {!isLoading ? (
              <>
                <div className="relative inline-block animate-bounce">
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-red-500 rounded-full blur-3xl opacity-50 animate-pulse"></div>
                  <FaEnvelope className="relative text-rose-400 text-7xl md:text-9xl mb-6 drop-shadow-2xl" />
                  <FaHeart className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-red-500 text-3xl md:text-5xl animate-pulse" />
                  <div className="absolute -top-4 -right-4 text-4xl md:text-6xl animate-spin-slow">💝</div>
                </div>
                <h2 className="text-white text-xl md:text-3xl font-bold mb-6 animate-pulse px-4">A Special Message For You...</h2>
                <button
                  onClick={openEnvelope}
                  className="bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 text-white px-8 py-4 md:px-14 md:py-6 rounded-full text-lg md:text-2xl font-bold hover:from-rose-700 hover:via-pink-700 hover:to-fuchsia-700 transition-all transform hover:scale-110 shadow-2xl animate-pulse border-4 border-white/30"
                >
                  Open My Heart 💕
                </button>
                <p className="text-pink-200 mt-4 text-sm md:text-lg animate-fade-in-up">Click to reveal your surprise...</p>
              </>
            ) : (
              <div className="relative">
                {/* Loading Hearts Animation */}
                <div className="mb-8">
                  <div className="relative inline-block">
                    {/* Animated Hearts Circle */}
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute"
                        style={{
                          top: '50%',
                          left: '50%',
                          transform: `rotate(${i * 45}deg) translateY(-80px)`,
                          animation: `heartbeat 1s ease-in-out infinite`,
                          animationDelay: `${i * 0.125}s`
                        }}
                      >
                        <FaHeart className="text-rose-400 text-3xl md:text-4xl" style={{
                          transform: 'rotate(-' + (i * 45) + 'deg)'
                        }} />
                      </div>
                    ))}
                    
                    {/* Center Heart */}
                    <div className="relative z-10">
                      <FaHeart className="text-red-500 text-6xl md:text-8xl animate-heartbeat drop-shadow-2xl" />
                    </div>
                  </div>
                </div>

                {/* Loading Text */}
                <h2 className="text-white text-2xl md:text-4xl font-bold mb-6 animate-pulse">
                  Opening My Heart For You... 💕
                </h2>

                {/* Progress Bar */}
                <div className="w-64 md:w-96 mx-auto bg-white/20 rounded-full h-4 md:h-6 overflow-hidden backdrop-blur-sm border-2 border-white/30">
                  <div 
                    className="bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-500 h-full rounded-full transition-all duration-300 relative overflow-hidden"
                    style={{ width: `${loadingProgress}%` }}
                  >
                    <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                  </div>
                </div>

                {/* Loading Percentage */}
                <p className="text-pink-200 text-xl md:text-2xl font-bold mt-4 animate-pulse">
                  {loadingProgress}%
                </p>

                {/* Floating Hearts */}
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`loading-heart-${i}`}
                      className="absolute animate-float"
                      style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 2}s`,
                        animationDuration: `${3 + Math.random() * 2}s`
                      }}
                    >
                      <span className="text-2xl md:text-3xl opacity-50">
                        {['❤️', '💕', '💖', '💗', '💝'][Math.floor(Math.random() * 5)]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="max-w-6xl w-full">
          {/* Header with Rotating Wishes */}
          <div className="text-center mb-6 md:mb-8 animate-fade-in-up px-4">
            <div className="flex justify-center mb-4 md:mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-500 rounded-full blur-3xl opacity-60 animate-pulse"></div>
                <FaHeart className="relative text-rose-400 text-6xl md:text-8xl animate-heartbeat drop-shadow-2xl" />
                <FaRing className="absolute -top-2 -right-2 md:-top-4 md:-right-4 text-yellow-300 text-3xl md:text-5xl animate-bounce" />
                <FaInfinity className="absolute -bottom-2 -left-2 md:-bottom-4 md:-left-4 text-pink-300 text-2xl md:text-4xl animate-spin-slow" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black bg-gradient-to-r from-rose-400 via-pink-500 to-fuchsia-500 bg-clip-text text-transparent mb-4 md:mb-6 animate-fade-in-up drop-shadow-2xl">
              Happy Valentine's Day! 💕
            </h1>
            <div className="h-20 md:h-24 flex items-center justify-center px-2">
              <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-pink-200 animate-pulse drop-shadow-lg">
                {wishes[currentWish]}
              </p>
            </div>
            {/* Rotating Love Quotes */}
            <div className="mt-4 md:mt-6 h-auto md:h-16 flex items-center justify-center px-2">
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-semibold text-rose-200 italic animate-fade-in-up">
                "{loveQuotes[currentQuote]}"
              </p>
            </div>
          </div>

          {/* Main Card */}
          {showMessage && (
            <div className="bg-white/5 backdrop-blur-2xl rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-12 border-2 md:border-4 border-rose-400/40 shadow-2xl animate-scale-in relative overflow-hidden">
              {/* Card Background Animation */}
              <div className="absolute inset-0 bg-gradient-to-br from-rose-500/10 via-pink-500/10 to-fuchsia-500/10 animate-pulse"></div>
              <div className="relative z-10">

              {/* Card 0: Photo */}
              {currentCard === 0 && (
                <div className="animate-fade-in-up">
              {/* Photo Section */}
              <div className="mb-8 md:mb-10">
                <div className="relative">
                  <div className="relative mx-auto max-w-sm md:max-w-md">
                    <div className="absolute inset-0 bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-500 rounded-2xl md:rounded-3xl blur-xl md:blur-2xl opacity-70 animate-pulse"></div>
                    <div className="relative bg-gradient-to-br from-rose-600 via-pink-600 to-fuchsia-600 p-2 md:p-3 rounded-2xl md:rounded-3xl animate-glow">
                      <img
                        src={sabariKalaiPhoto}
                        alt="Sabari & Kalai"
                        className="w-full h-auto rounded-xl md:rounded-2xl object-contain shadow-2xl transform hover:scale-105 hover:rotate-1 transition-all duration-500"
                      />
                      {/* Corner Hearts */}
                      <div className="absolute -top-2 -left-2 md:-top-3 md:-left-3 text-2xl md:text-4xl animate-bounce">💖</div>
                      <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 text-2xl md:text-4xl animate-bounce" style={{ animationDelay: '0.2s' }}>💕</div>
                      <div className="absolute -bottom-2 -left-2 md:-bottom-3 md:-left-3 text-2xl md:text-4xl animate-bounce" style={{ animationDelay: '0.4s' }}>💝</div>
                      <div className="absolute -bottom-2 -right-2 md:-bottom-3 md:-right-3 text-2xl md:text-4xl animate-bounce" style={{ animationDelay: '0.6s' }}>💗</div>
                    </div>
                    <div className="absolute -bottom-4 md:-bottom-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 px-4 py-2 md:px-8 md:py-3 rounded-full shadow-2xl border-2 md:border-4 border-white/30 animate-pulse">
                      <p className="text-white font-bold text-sm md:text-xl drop-shadow-lg whitespace-nowrap">Sabari & Kalai 129 💖</p>
                    </div>
                  </div>
                </div>
              </div>
              </div>
              )}

              {/* Card 1: Love Message */}
              {currentCard === 1 && (
                <div className="animate-fade-in-up">
              {/* Love Message */}
              <div className="text-center mb-8 md:mb-10 mt-8 md:mt-12">
                <div className="bg-gradient-to-r from-rose-500/20 via-pink-500/20 to-fuchsia-500/20 rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 border-2 border-rose-400/50 backdrop-blur-sm animate-glow">
                  <div className="flex justify-center mb-4 md:mb-6">
                    <FaMusic className="text-rose-400 text-2xl md:text-4xl animate-bounce mr-2 md:mr-4" />
                    <FaHeart className="text-red-400 text-4xl md:text-6xl animate-heartbeat" />
                    <FaMusic className="text-rose-400 text-2xl md:text-4xl animate-bounce ml-2 md:ml-4" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-300 via-pink-300 to-rose-300 bg-clip-text text-transparent mb-4 md:mb-6 drop-shadow-lg animate-pulse">
                    To My Beloved Kalai! 💕
                  </h2>
                  <div className="space-y-4 md:space-y-6 text-pink-100 text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed">
                    <p className="animate-fade-in-up font-bold text-rose-200" style={{ animationDelay: '0.3s' }}>
                      You are my backbone, my strength, my everything! 💪❤️
                    </p>
                    <p className="animate-fade-in-up font-semibold" style={{ animationDelay: '0.5s' }}>
                      Even though you're in training, my heart is always with you! 💖
                    </p>
                    <p className="animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
                      I will always be by your side to help your dreams come true! 🌟
                    </p>
                    <p className="animate-fade-in-up font-bold text-lg md:text-2xl text-pink-200" style={{ animationDelay: '0.9s' }}>
                      You're not just my love, you're my life's purpose! 💝
                    </p>
                    <p className="animate-fade-in-up font-bold text-xl md:text-3xl bg-gradient-to-r from-rose-300 to-pink-300 bg-clip-text text-transparent" style={{ animationDelay: '1.1s' }}>
                      This Valentine's Day is only for you! ❤️
                    </p>
                  </div>
                </div>
              </div>
              </div>
              )}

              {/* Card 2: Special Features */}
              {currentCard === 2 && (
                <div className="animate-fade-in-up">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-10">
                <div className="bg-gradient-to-br from-rose-600 via-pink-600 to-red-600 p-6 md:p-8 rounded-2xl md:rounded-3xl text-center transform hover:scale-110 hover:rotate-2 transition-all shadow-2xl border-2 md:border-4 border-white/30 animate-glow">
                  <FaHeart className="text-white text-4xl md:text-6xl mx-auto mb-3 md:mb-4 animate-heartbeat drop-shadow-lg" />
                  <h3 className="text-white font-bold text-lg md:text-2xl mb-2 md:mb-3">My Backbone</h3>
                  <p className="text-pink-100 text-sm md:text-lg mb-2">You give me strength</p>
                  <p className="text-white font-bold text-2xl md:text-4xl">💪❤️</p>
                </div>

                <div className="bg-gradient-to-br from-fuchsia-600 via-pink-600 to-rose-600 p-6 md:p-8 rounded-2xl md:rounded-3xl text-center transform hover:scale-110 hover:rotate-2 transition-all shadow-2xl border-2 md:border-4 border-white/30 animate-glow" style={{ animationDelay: '0.2s' }}>
                  <FaInfinity className="text-white text-4xl md:text-6xl mx-auto mb-3 md:mb-4 animate-spin-slow drop-shadow-lg" />
                  <h3 className="text-white font-bold text-lg md:text-2xl mb-2 md:mb-3">Forever Love</h3>
                  <p className="text-purple-100 text-sm md:text-lg mb-2">Always with you</p>
                  <p className="text-white font-bold text-2xl md:text-4xl">∞</p>
                </div>

                <div className="bg-gradient-to-br from-pink-600 via-rose-600 to-red-600 p-6 md:p-8 rounded-2xl md:rounded-3xl text-center transform hover:scale-110 hover:rotate-2 transition-all shadow-2xl border-2 md:border-4 border-white/30 animate-glow sm:col-span-2 md:col-span-1" style={{ animationDelay: '0.4s' }}>
                  <FaStar className="text-white text-4xl md:text-6xl mx-auto mb-3 md:mb-4 animate-spin-slow drop-shadow-lg" />
                  <h3 className="text-white font-bold text-lg md:text-2xl mb-2 md:mb-3">My Everything</h3>
                  <p className="text-red-100 text-sm md:text-lg mb-2">You are my world</p>
                  <p className="text-white font-bold text-2xl md:text-4xl">🌍💫</p>
                </div>
              </div>
              </div>
              )}

              {/* Card 3: True Love Quotes */}
              {currentCard === 3 && (
                <div className="animate-fade-in-up">
              {/* True Love Quotes Section */}
              <div className="bg-gradient-to-r from-rose-500/30 via-pink-500/30 to-fuchsia-500/30 rounded-2xl md:rounded-3xl p-6 md:p-10 mb-8 md:mb-10 border-2 md:border-4 border-rose-400/50 shadow-2xl backdrop-blur-sm animate-glow">
                <div className="text-center mb-4 md:mb-6">
                  <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent mb-4">
                    True Love Quotes 💕
                  </h3>
                </div>
                <div className="space-y-4 md:space-y-6">
                  <div className="bg-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 backdrop-blur-sm transform hover:scale-105 transition-all">
                    <p className="text-white text-center text-base sm:text-lg md:text-xl lg:text-2xl italic font-semibold leading-relaxed drop-shadow-lg">
                      "Love is not about how many days, months, or years you've been together.<br className="hidden sm:block" />
                      Love is about how much you love each other every single day."
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 backdrop-blur-sm transform hover:scale-105 transition-all">
                    <p className="text-white text-center text-base sm:text-lg md:text-xl lg:text-2xl italic font-semibold leading-relaxed drop-shadow-lg">
                      "You are my today and all of my tomorrows."
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 backdrop-blur-sm transform hover:scale-105 transition-all">
                    <p className="text-white text-center text-base sm:text-lg md:text-xl lg:text-2xl italic font-semibold leading-relaxed drop-shadow-lg">
                      "In you, I've found the love of my life and my closest, truest friend."
                    </p>
                  </div>
                </div>
                <p className="text-rose-300 text-center mt-6 md:mt-8 text-lg md:text-2xl font-bold animate-pulse">
                  - Your Sabarisan 💕
                </p>
              </div>
              </div>
              )}

              {/* Card 4: My Promises */}
              {currentCard === 4 && (
                <div className="animate-fade-in-up">
              {/* Promise Section */}
              <div className="bg-gradient-to-r from-yellow-500/30 via-orange-500/30 to-rose-500/30 rounded-2xl md:rounded-3xl p-6 md:p-8 mb-8 md:mb-10 border-2 md:border-4 border-yellow-400/50 shadow-2xl backdrop-blur-sm animate-glow">
                <h3 className="text-yellow-200 text-2xl md:text-3xl font-bold text-center mb-6 md:mb-8 drop-shadow-lg animate-pulse">My Promise to You 🤝</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 text-white text-base md:text-lg">
                  <div className="flex items-start space-x-3 md:space-x-4 bg-white/10 p-4 md:p-5 rounded-xl md:rounded-2xl backdrop-blur-sm transform hover:scale-105 transition-all">
                    <span className="text-3xl md:text-4xl flex-shrink-0">💪</span>
                    <p className="font-semibold">I'll be your backbone and support you until your training is complete</p>
                  </div>
                  <div className="flex items-start space-x-3 md:space-x-4 bg-white/10 p-4 md:p-5 rounded-xl md:rounded-2xl backdrop-blur-sm transform hover:scale-105 transition-all">
                    <span className="text-3xl md:text-4xl flex-shrink-0">🎯</span>
                    <p className="font-semibold">I'll help make all your dreams come true</p>
                  </div>
                  <div className="flex items-start space-x-3 md:space-x-4 bg-white/10 p-4 md:p-5 rounded-xl md:rounded-2xl backdrop-blur-sm transform hover:scale-105 transition-all">
                    <span className="text-3xl md:text-4xl flex-shrink-0">🌟</span>
                    <p className="font-semibold">I'll always motivate and inspire you to reach new heights</p>
                  </div>
                  <div className="flex items-start space-x-3 md:space-x-4 bg-white/10 p-4 md:p-5 rounded-xl md:rounded-2xl backdrop-blur-sm transform hover:scale-105 transition-all">
                    <span className="text-3xl md:text-4xl flex-shrink-0">❤️</span>
                    <p className="font-semibold">I'll love you forever and always, no matter what</p>
                  </div>
                  <div className="flex items-start space-x-3 md:space-x-4 bg-white/10 p-4 md:p-5 rounded-xl md:rounded-2xl backdrop-blur-sm transform hover:scale-105 transition-all">
                    <span className="text-3xl md:text-4xl flex-shrink-0">🏠</span>
                    <p className="font-semibold">You are my home, my peace, my everything</p>
                  </div>
                  <div className="flex items-start space-x-3 md:space-x-4 bg-white/10 p-4 md:p-5 rounded-xl md:rounded-2xl backdrop-blur-sm transform hover:scale-105 transition-all">
                    <span className="text-3xl md:text-4xl flex-shrink-0">💫</span>
                    <p className="font-semibold">Together, we'll create a beautiful future</p>
                  </div>
                </div>
              </div>
              </div>
              )}

              {/* Card 5: I Love You */}
              {currentCard === 5 && (
                <div className="animate-fade-in-up">
              {/* Big I Love You Message */}
              <div className="text-center mb-8 md:mb-10">
                <div className="inline-block bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 p-8 md:p-12 rounded-2xl md:rounded-3xl shadow-2xl transform hover:scale-105 transition-all border-2 md:border-4 border-white/40 animate-glow">
                  <p className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-3 md:mb-4 animate-heartbeat drop-shadow-2xl">
                    I LOVE YOU! ❤️
                  </p>
                  <p className="text-pink-100 text-xl sm:text-2xl md:text-3xl font-bold mb-2 md:mb-3">
                    Kalai, You are my backbone, my life! 💖
                  </p>
                  <p className="text-pink-200 text-lg md:text-xl mb-2 md:mb-3">
                    You give me strength to face anything! 💪
                  </p>
                  <p className="text-rose-100 text-base md:text-xl">
                    I'll be waiting until your training is complete! 🌹
                  </p>
                  <div className="mt-4 md:mt-6 flex justify-center space-x-3 md:space-x-4">
                    <span className="text-3xl md:text-5xl animate-bounce">💕</span>
                    <span className="text-3xl md:text-5xl animate-bounce" style={{ animationDelay: '0.2s' }}>💖</span>
                    <span className="text-3xl md:text-5xl animate-bounce" style={{ animationDelay: '0.4s' }}>💝</span>
                  </div>
                </div>
              </div>
              </div>
              )}

              {/* Card 6: Long Distance Love */}
              {currentCard === 6 && (
                <div className="animate-fade-in-up">
              {/* Long Distance Love Message */}
              <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-2xl md:rounded-3xl p-6 md:p-8 mb-6 md:mb-8 border-2 md:border-4 border-purple-400/50 shadow-xl backdrop-blur-sm animate-glow">
                <div className="flex items-center justify-center mb-3 md:mb-4">
                  <FaCalendarAlt className="text-pink-400 text-2xl md:text-3xl mr-2 md:mr-4" />
                  <FaHeart className="text-red-500 text-3xl md:text-4xl animate-heartbeat" />
                  <FaCalendarAlt className="text-pink-400 text-2xl md:text-3xl ml-2 md:ml-4" />
                </div>
                <p className="text-white text-center text-lg sm:text-xl md:text-2xl font-bold mb-3 md:mb-4">
                  💫 Distance means so little when you mean so much! 💫
                </p>
                <p className="text-pink-200 text-center text-base sm:text-lg md:text-xl mb-2 md:mb-3">
                  Every moment without you feels like forever... 🥺
                </p>
                <p className="text-rose-200 text-center text-base sm:text-lg md:text-xl mb-2 md:mb-3">
                  I miss your smile, your voice, your presence every single day! 💭
                </p>
                <p className="text-pink-300 text-center text-base sm:text-lg md:text-xl font-semibold">
                  But our love is stronger than any distance! 💪❤️
                </p>
                <div className="mt-4 md:mt-6 text-center">
                  <p className="text-yellow-200 text-lg md:text-xl font-bold animate-pulse">
                    Counting down the days until we're together again! ⏰💕
                  </p>
                </div>
              </div>
              </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 mb-4">
                {currentCard > 0 && (
                  <button
                    onClick={previousCard}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-full text-base md:text-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-110 shadow-xl"
                  >
                    ← Previous
                  </button>
                )}
                {currentCard < 6 && (
                  <button
                    onClick={nextCard}
                    className="bg-gradient-to-r from-rose-600 to-fuchsia-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-full text-base md:text-lg font-bold hover:from-rose-700 hover:to-fuchsia-700 transition-all transform hover:scale-110 shadow-xl ml-auto"
                  >
                    Next →
                  </button>
                )}
              </div>

              {/* Back Button */}
              <div className="text-center">
                <Link
                  to="/"
                  className="inline-block bg-white text-pink-600 px-8 py-3 md:px-10 md:py-4 rounded-full text-lg md:text-xl font-bold hover:bg-pink-100 transition-all transform hover:scale-110 shadow-2xl"
                >
                  ← Back to Portfolio
                </Link>
              </div>
              </div>
            </div>
          )}
          {/* Footer Message */}
          <div className="text-center mt-10 animate-fade-in" style={{ animationDelay: '2s' }}>
            <p className="text-pink-200 text-xl font-bold drop-shadow-lg">
              Made with 💖 by Sabarisan P (Your Sabari)
            </p>
            <p className="text-pink-300 text-lg mt-2">
              For My Lovely Kalai 💕
            </p>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 text-8xl animate-pulse opacity-20 drop-shadow-2xl">💝</div>
      <div className="absolute bottom-10 right-10 text-8xl animate-spin-slow opacity-20 drop-shadow-2xl" style={{ animationDelay: '1s' }}>🌹</div>
      <div className="absolute top-1/4 right-20 text-6xl animate-bounce opacity-30 drop-shadow-xl">💕</div>
      <div className="absolute bottom-1/3 left-20 text-6xl animate-bounce opacity-30 drop-shadow-xl" style={{ animationDelay: '0.5s' }}>💖</div>
      <div className="absolute top-1/2 left-10 text-5xl animate-pulse opacity-30 drop-shadow-xl">💐</div>
      <div className="absolute bottom-1/4 right-10 text-5xl animate-pulse opacity-30 drop-shadow-xl" style={{ animationDelay: '0.7s' }}>🎁</div>
    </div>
  );
};

export default ValentinePage;
